<script lang="ts">
    import {getLocalization} from 'src/core';
    import type {UserPreference} from 'src/typings';
    import type {ValueEditorLabelContext} from 'shared/value-editors/internal/label/value-editor-label-context';
    import type {KpUserPreferencesModalService} from './kp-user-preferences-modal.service';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {exists} from 'shared/utils/custom-utils';
    import {createEventDispatcher, tick} from 'svelte';
    import {createValueEditorsLabelContext} from 'shared/value-editors/internal/label/value-editor-label-context';
    import {slide} from 'svelte/transition';
    import KpDatatypedValue from 'shared/components/kp-datatyped/KpDatatypedValue.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import KpValueEditor from 'shared/value-editors/kp-value-editor/KpValueEditor.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';

    export let userPreference: UserPreference;
    export let service: KpUserPreferencesModalService;
    const localize = getLocalization();
    const dispatch = createEventDispatcher<{ update: UserPreference }>();
    const slideAnimParams = {duration: 250};

    let editMode = false;
    let saving = false;
    let formElement: HTMLFormElement;

    function createPreferenceEditorLabelContext(): ValueEditorLabelContext {
        return createValueEditorsLabelContext(`user-preference-${userPreference.keyId}-editor`, userPreference.keyText);
    }

    const togglePreferenceEditing = () => {
        editMode = !editMode;

        tick().then(() => {
            if (editMode && exists(formElement)) {
                formElement.focus();
            }
        });
    }

    const setToDefault = async () => {
        saving = true;
        const updatedPreference = await service.setPreferenceToDefault(userPreference);
        dispatch('update', updatedPreference);
        editMode = false;
        saving = false;
    }

    const savePreference = async () => {
        saving = true;
        const savedPreference = await service.savePreference(userPreference);
        dispatch('update', savedPreference);
        editMode = false;
        saving = false;
    }
</script>

<tr class="user-preference-{userPreference.keyId}">
    <th>{pipe(userPreference.keyText, loc())}</th>

    <td>
        <div class:text-muted={userPreference.customized}
             class:overwritten-value={userPreference.customized}
             aria-hidden={userPreference.customized}>

            {#if !exists(userPreference.datatype.nestedDatatype)}
                <KpDatatypedValue value={userPreference.defaultValue}
                                  datatype={userPreference.datatype}/>
            {/if}

            {#if exists(userPreference.datatype.nestedDatatype)}
                <KpDatatypedValue value={userPreference.defaultValue}
                                  datatype={userPreference.datatype}
                                  fallback={localize(/* @kp-localization commons.Vse */ 'commons.Vse')}/>
            {/if}
        </div>

        {#if userPreference.customized || editMode}
            <div class="arrow-container" aria-hidden="true">
                <UIcon icon="arrow-down"/>
            </div>
        {/if}

        {#if userPreference.customized && !editMode}
            <KpDatatypedValue value={userPreference.customValue}
                              datatype={userPreference.datatype}
                              fallback={localize(/* @kp-localization commons.Vse */ 'commons.Vse')}/>
        {/if}

        {#if editMode}
            {@const preferenceEditorLabelContext = createPreferenceEditorLabelContext()}

            <div in:slide={slideAnimParams}>
                <form tabindex="-1" id="user-preference-{userPreference.keyId}-form" bind:this={formElement}>
                    <div class="form-group">
                        <label for={preferenceEditorLabelContext.editorId}
                               id={preferenceEditorLabelContext.labelId}
                               class="sr-only">
                            {preferenceEditorLabelContext.labelText}
                        </label>

                        <KpValueEditor {...userPreference.editor}
                                       editorId={preferenceEditorLabelContext.editorId}
                                       bind:model={userPreference.editedValue}/>
                    </div>
                </form>

                <KpButton buttonStyle="success"
                          dataQa="user-preferences-{userPreference.keyId}-save-button"
                          on:click={savePreference}>

                    <IconedContent icon="check">
                        {localize(/* @kp-localization commons.Ulozit */ 'commons.Ulozit')}
                    </IconedContent>
                </KpButton>
            </div>
        {/if}

        {#if saving}
            <div>
                <KpLoadingBlock size="xs"/>
            </div>
        {/if}
    </td>

    <td class="action-buttons-container">
        {#if userPreference.editable}
            <KpButton buttonSize="sm"
                      dataQa="user-preferences-{userPreference.keyId}-change-button"
                      aria-controls="user-preference-{userPreference.keyId}-form"
                      on:click={togglePreferenceEditing}>

                {localize(/* @kp-localization commons.Change */ 'commons.Change')}
            </KpButton>

            <KpButton buttonSize="sm" on:click={setToDefault}>
                {localize(/* @kp-localization commons.SetDefault */ 'commons.SetDefault')}
            </KpButton>
        {/if}
    </td>
</tr>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .arrow-container {
        margin: @spacing-s 0;
    }

    .action-buttons-container {
        text-align: end;
    }
</style>